"use client";

import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { Eye, Loader2, X } from "lucide-react";
import { useAuth } from "@/components/auth/AuthProvider";
import { hasMemberAccess } from "@/lib/authz/policy";
import LandingView from "@/views/Landing";
import CreatorHomeView from "@/views/CreatorHome";
import FreeHomeView from "@/views/FreeHome";
import { useIsPreviewingFreeUser } from "@/lib/home/freePreview";
import { usePreviewPlan } from "@/lib/home/planPreview";
import type { Announcement } from "@/lib/landing/announcement";

/**
 * Decides which homepage to render at `/`:
 *
 *  - Signed out                        → Marketing landing (sales-focused).
 *  - Signed-in subscribers (or admins) → CreatorHome (work-focused: nav,
 *    "welcome back" hero, goal cards, full tool grid, library).
 *  - Signed in, no paid access         → FreeHome (free tools + upgrade path).
 *
 * Admin preview: admins normally only ever see CreatorHome, so `/?preview=landing`
 * lets them inspect the marketing landing, and `/?preview=free` (the "view as a
 * free user" toggle in AppHeader — see src/lib/home/freePreview.ts) lets them
 * inspect FreeHome, both without logging out. The landing preview is marked by
 * a floating pill rendered here (page-scoped, since `?preview=landing` isn't
 * persisted). The free-user preview is instead marked by a pill rendered
 * inside AppHeader itself, because that flag is persisted to sessionStorage
 * and must stay visible across client-side navigation, not just on `/`.
 *
 * First paint (while client auth is still resolving): `hasAuthCookie` is read
 * server-side (presence only) so we can render the right shell immediately —
 * anonymous visitors get the SSR'd marketing landing (no blank page), while
 * likely-subscribers get a minimal loader to avoid a landing→CreatorHome flash.
 *
 * `announcement` is likewise resolved server-side and handed to every landing
 * render below (including the admin preview) so the bar is present in the first
 * paint instead of popping in and pushing the hero down.
 */
export function HomePageSwitcher({
  hasAuthCookie,
  announcement = null,
}: {
  hasAuthCookie?: boolean;
  announcement?: Announcement | null;
}) {
  const { user, billing, isAdmin, permissions, loading } = useAuth();
  const searchParams = useSearchParams();
  const previewingFreeUser = useIsPreviewingFreeUser(isAdmin);
  const previewPlan = usePreviewPlan(isAdmin);

  if (loading) {
    // No auth cookie → definitely anonymous → render the marketing landing now
    // (this is what gets SSR'd on first request). Otherwise show a loader while
    // the session resolves so a subscriber never flashes the landing first.
    if (!hasAuthCookie) return <LandingView announcement={announcement} />;
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" aria-label="Loading" />
      </div>
    );
  }

  const previewLanding = isAdmin && searchParams.get("preview") === "landing";
  if (previewLanding) {
    return (
      <>
        <LandingView announcement={announcement} />
        <AdminPreviewPill label="Previewing landing page" />
      </>
    );
  }

  // Admin "view as a free user" override: unconditional, exactly like
  // previewLanding above — it always wins over the real hasPaidAccess check
  // below so it faithfully shows what a free user sees even if this admin's
  // account happens to carry an incidental subscription. `previewingFreeUser`
  // is already ANDed against the real `isAdmin` inside the hook, so this
  // branch can only ever be reached by a genuine admin. The hook is backed by
  // sessionStorage, so this branch (and the header nav it renders via
  // FreeHomeView → AppHeader) keeps winning across client-side navigation,
  // not just while `?preview=free` is literally in the URL. No pill is
  // rendered here — AppHeader itself shows the "Viewing as a free user" /
  // Exit escape hatch, fixed on every route, so it doesn't need duplicating
  // per-page.
  if (previewingFreeUser) {
    return <FreeHomeView viewingAsFreeUser />;
  }

  // Admin "Preview as plan" (src/lib/home/planPreview.ts): always the
  // subscriber home — CreatorHome locks/unlocks tools per the previewed plan.
  // Already ANDed against the real `isAdmin` inside the hook.
  if (previewPlan) {
    return <CreatorHomeView />;
  }

  // Includes accounts with admin-granted tools but no subscription.
  const hasPaidAccess = hasMemberAccess({ isAdmin, billing, permissions });

  if (user && hasPaidAccess) {
    return <CreatorHomeView />;
  }

  if (user) {
    return <FreeHomeView />;
  }

  return <LandingView announcement={announcement} />;
}

function AdminPreviewPill({ label }: { label: string }) {
  return (
    <div className="fixed bottom-6 left-1/2 z-[60] -translate-x-1/2">
      <div className="flex items-center gap-3 rounded-full border border-border/70 bg-background/95 px-4 py-2 text-sm shadow-lg backdrop-blur">
        <span className="flex items-center gap-2 font-medium text-foreground">
          <Eye className="h-4 w-4 text-accent" />
          {label}
        </span>
        <span className="h-4 w-px bg-border/70" aria-hidden />
        <Link
          href="/"
          className="inline-flex items-center gap-1 text-muted-foreground transition hover:text-foreground"
        >
          <X className="h-3.5 w-3.5" />
          Exit
        </Link>
      </div>
    </div>
  );
}
